import {
  Flex,
  Spinner,
  Table,
  TableCaption,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import {
  getImportReportSheetRows,
  ImportReportRowModel,
  ImportReportSheetModel,
} from "../../clients/BackOfficeApi";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import {
  usePagination,
  withPagination,
} from "../../contexts/PaginationContext";
import { ImportReportSheetRow } from "./ImportReportSheetRow";
import { Issue } from "../../elements/Issue";
import { PaginationForm } from "../../elements/PaginationForm";
import { TableLoadingRow } from "../../elements/TableLoadingRow";

interface ImportReportSheetProps {
  sheet: ImportReportSheetModel;
}

export const ImportReportSheet = withPagination<ImportReportSheetProps>(
  {
    settingsKey: "importReportSheetRows",
    defaultValue: 50,
    options: [10, 25, 50, 100, 200, 500],
  },
  ({ sheet }: ImportReportSheetProps) => {
    const [rows, setRows] = useState<ImportReportRowModel[]>([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const { offset, limit } = usePagination();
    const [loadedOffset, setLoadedOffset] = useState(0);

    useLayoutEffect(() => {
      setLoading(true);

      const abortController = new AbortController();

      const query = { Offset: offset, Limit: limit };

      getImportReportSheetRows({
        path: { importReportSheetId: sheet.id },
        query,
        signal: abortController.signal,
        throwOnError: true,
      })
        .then(({ data }) => {
          if (abortController.signal.aborted) return;

          const items = data?.items ?? [];

          const loadedItems = items.length;
          setLoadedOffset(query.Offset + loadedItems);
          setRows((prev) => {
            prev.splice(query.Offset, loadedItems, ...items);
            return prev.slice(0, query.Offset + loadedItems);
          });
          setTotal(data?.totalCount ?? 0);
        })
        .catch((e) => {
          if (abortController.signal.aborted) return;

          throw e;
        })
        .finally(() => setLoading(false));

      return () => abortController.abort("Component unmounted or re-rendered");
    }, [sheet.id, offset, limit]);

    const moreRowsAvailable = useMemo(
      () => loadedOffset < total,
      [loadedOffset, total],
    );

    if (!loading && !rows.length) return null;

    return (
      <>
        <Text>Gevonden problemen met tabblad: {sheet.issues.length}</Text>
        {sheet.issues.map((issue) => (
          <Flex key={issue.id}>
            <Issue issue={issue} />
          </Flex>
        ))}
        <Table mt={4} size="sm" variant="striped">
          <TableCaption placement="top">Aantal rijen: {total}</TableCaption>
          <Thead>
            <Tr>
              {sheet.columns.map((c) => (
                <Th key={c.id} minW={40}>
                  {c.name} ({c.type})
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            <TableLoadingRow
              colSpan={sheet.columns.length}
              loading={!moreRowsAvailable && loading}
            />
            {rows.map((row) => (
              <ImportReportSheetRow key={row.id} row={row} />
            ))}
            {moreRowsAvailable && (
              <Tr>
                <Td colSpan={sheet.columns.length}>
                  <PaginationForm loading={loading} offset={loadedOffset} />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </>
    );
  },
);
